import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import * as moment from 'moment';
import { VariableBenefitAccountDetails } from '../../../../../../models/transactions/variable-benefit-account-details';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';

export interface VbCurrentRecurringPaymentRow {
  governingJurisdictionId: number | null;
  paymentAmount: number;
  frequencyLabel: string;
  amountTypeLabel: string;
}

@Component({
  selector: 'app-vb-current-recurring-payments-card',
  templateUrl: './vb-current-recurring-payments-card.component.html',
  styleUrls: ['./vb-current-recurring-payments-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VbCurrentRecurringPaymentsCardComponent {
  @Input({ required: true }) set accountDetails(value: VariableBenefitAccountDetails[]) {
    this._accountDetails.next(value);
  }

  private _accountDetails = new BehaviorSubject<VariableBenefitAccountDetails[]>([]);

  private activeDetails$ = this._accountDetails.pipe(
    map(details => details.filter(d => d.activeRecurringPaymentInstruction !== null))
  );

  public rows$: Observable<VbCurrentRecurringPaymentRow[]> = this.activeDetails$.pipe(
    map(details =>
      details.map(d => {
        const instruction = d.activeRecurringPaymentInstruction!;
        const isAnnual = instruction.paymentFrequencyId === VariableBenefitPaymentFrequencyEnum.Annual;
        const amountTypeLabel = instruction.useMinimumAmount ? $localize`Minimum` : $localize`Custom`;
        return {
          governingJurisdictionId: d.governingJurisdictionId,
          paymentAmount: instruction.paymentAmount,
          frequencyLabel: isAnnual ? $localize`/yr` : $localize`/mo`,
          amountTypeLabel: instruction.useMaximumAmount ? $localize`Maximum` : amountTypeLabel
        };
      })
    )
  );

  public total$: Observable<number> = this.activeDetails$.pipe(
    map(details => details.reduce((sum, d) => sum + (d.activeRecurringPaymentInstruction?.paymentAmount ?? 0), 0))
  );

  public frequencyLabel$: Observable<string> = this.activeDetails$.pipe(
    map(details => {
      const first = details[0]?.activeRecurringPaymentInstruction;
      if (!first) return '';
      const isAnnual = first.paymentFrequencyId === VariableBenefitPaymentFrequencyEnum.Annual;
      return isAnnual ? $localize`/yr` : $localize`/mo`;
    })
  );

  public paidInMonth$: Observable<string | null> = this.activeDetails$.pipe(
    map(details => {
      const first = details[0]?.activeRecurringPaymentInstruction;
      if (!first) return null;
      if (first.paymentFrequencyId !== VariableBenefitPaymentFrequencyEnum.Annual) return null;
      return moment(first.paymentStartDate).format('MMMM');
    })
  );
}
